import React, { useState, useEffect, useMemo } from 'react';
import { Paper, IconButton, Chip, Box, TextField, InputAdornment, TableRow, TableCell, Typography, MenuItem, Autocomplete } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from '../DataTable';
import * as API from '../../api';
import SupplierDialog from './SupplierDialog';


const TYPE_MAP = {
  'TOUR': { label: 'Đại lý Tour', color: 'primary' },
  'TRANSPORT': { label: 'Nhà xe', color: 'secondary' },
  'GUIDE': { label: 'HDV', color: 'success' },
  'OTHER': { label: 'Khác', color: 'default' }
};


const SupplierTab = ({ openAddSignal, onCloseAdd }) => {
  const [data, setData] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL'); // State lọc loại NCC
  const [filterProvince, setFilterProvince] = useState(null);
  const [locations, setLocations] = useState([]);

  const userRole = localStorage.getItem('user_role') || 'SALE';

  const loadData = async () => {
    try {
      const [supRes, locRes] = await Promise.all([
        API.fetchAllServiceProviders(),
        API.client.get('/locations/')
      ]);
      setData(supRes.data || []);
      setLocations(locRes.data || []);
    } catch (e) { console.error(e); }
  };


  useEffect(() => { loadData(); }, []);


  useEffect(() => {
    if (openAddSignal) {
      setEditItem(null);
      setIsOpen(true);
    }
  }, [openAddSignal]);


  const handleDelete = async (id) => {
    if (window.confirm('Bạn chắc chắn muốn xóa NCC này?')) {
      try {
        await API.deleteServiceProvider(id);
        loadData();
      } catch (e) { alert('Không thể xóa (Có thể do ràng buộc dữ liệu)'); }
    }
  };


  const filteredData = useMemo(() => {
    return data.filter(item => {
      // 1. Lọc theo tìm kiếm từ khóa
      let matchSearch = true;
      if (searchTerm) {
          const lowerKey = searchTerm.toLowerCase();
          matchSearch = (item.name && item.name.toLowerCase().includes(lowerKey)) ||
                        (item.phone && item.phone.includes(lowerKey));
      }
      
      // 2. Lọc theo danh mục dịch vụ
      let matchCategory = true;
      if (filterCategory !== 'ALL') {
          // Kiểm tra xem mã filterCategory có nằm trong danh sách categories của item không
          matchCategory = item.categories_detail?.some(cat => cat.code === filterCategory);
      }

      // 3. Lọc theo Tỉnh/Thành phố
      let matchProvince = true;
      if (filterProvince) {
          matchProvince = (item.province === filterProvince.name);
      }

      return matchSearch && matchCategory && matchProvince;
    }, [data, searchTerm, filterCategory, filterProvince]);
  })


  const columns = [
    { label: 'Tên NCC', field: 'name', minWidth: 200 },
    { label: 'Loại', field: 'service_type', minWidth: 120 },
    { label: 'Địa chỉ', field: 'location', minWidth: 150 },
    { label: 'Người liên hệ', field: 'contact_person', minWidth: 150 },
    { label: 'SĐT', field: 'phone', minWidth: 120 },
    { label: 'Email', field: 'email', minWidth: 150 },
    { label: 'TT Thanh toán', field: 'bank_info', minWidth: 200 },
    { label: '', field: 'action', minWidth: 100 }
  ];

  return (
    <Paper sx={{ p: 2 }}>
      {/* Box Công cụ: Tìm kiếm và Lọc */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 250, flex: 1 }}>
            <TextField
                size="small" fullWidth
                placeholder="Tìm kiếm NCC theo tên, sđt..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
            />
        </Box>
        <Box sx={{ minWidth: 200 }}>
            <TextField
                select size="small" fullWidth
                label="Lọc theo Loại NCC"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
            >
                <MenuItem value="ALL">-- Tất cả loại dịch vụ --</MenuItem>
                <MenuItem value="ACCOMMODATION">Dịch vụ lưu trú</MenuItem>
                <MenuItem value="TRANSPORT">Lữ hành & Vận chuyển</MenuItem>
                <MenuItem value="FNB">Dịch vụ ăn uống</MenuItem>
                <MenuItem value="ENTERTAINMENT_SPORTS">Vui chơi, Thể thao</MenuItem>
                <MenuItem value="SUPPORT">Hỗ trợ & Thông tin</MenuItem>
                <MenuItem value="OTHER">Dịch vụ bổ trợ</MenuItem>
            </TextField>
        </Box>
        <Box sx={{ minWidth: 180 }}>
            <Autocomplete
                size="small"
                options={locations}
                getOptionLabel={(opt) => opt.name || ''}
                value={filterProvince}
                onChange={(e, val) => setFilterProvince(val)}
                renderInput={(params) => <TextField {...params} label="Lọc theo Tỉnh/Thành" />}
            />
        </Box>
      </Box>

      <DataTable
        columns={columns}
        data={filteredData}
        renderRow={(row) => (
          // Sử dụng TableRow và TableCell để có giao diện chuẩn (đường kẻ mờ)
          <TableRow key={row.id} hover>
            <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.name}</TableCell>
            <TableCell>
              {row.categories_detail && row.categories_detail.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {row.categories_detail.map(cat => (
                          <Chip
                              key={cat.code}
                              label={cat.name.split('(')[0].trim()} // Lấy tên ngắn gọn bỏ phần trong ngoặc
                              color={cat.code === 'TRANSPORT' ? 'secondary' : cat.code === 'ACCOMMODATION' ? 'primary' : 'default'}
                              size="small" 
                              variant="outlined"
                          />
                      ))}
                  </Box>
              ) : (
                  <span style={{ color: '#999' }}>Chưa phân loại</span>
              )}
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.province || '---'}</Typography>
                {row.address && (
                    <Typography variant="caption" color="textSecondary" display="block">
                        {row.address}
                    </Typography>
                )}
            </TableCell>
            <TableCell>{row.contact_person}</TableCell>
            <TableCell>{row.phone}</TableCell>
            <TableCell>
                <Typography variant="body2">{row.email || '---'}</Typography>
                {row.cc_emails && (
                    <Typography variant="caption" color="textSecondary" display="block">
                        <strong>CC:</strong> {row.cc_emails}
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                    {row.bank_info}
                </Box>
                {row.payment_qr && (
                    <Chip label="Có QR" size="small" color="info" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                )}
            </TableCell>
            <TableCell align="center">
              {['MANAGER', 'SOURCING'].includes(userRole) ? (
                <>
                  <IconButton size="small" color="primary" onClick={() => { setEditItem(row); setIsOpen(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
               </>
              ) : (
                <Typography variant="caption" color="text.secondary">Chỉ xem</Typography>
              )}
            </TableCell>
          </TableRow>
        )}
      />
      <SupplierDialog
        open={isOpen}
        onClose={() => { setIsOpen(false); onCloseAdd(); }}
        initialData={editItem}
        onSuccess={() => { setIsOpen(false); onCloseAdd(); loadData(); }}
      />
    </Paper>
  );
};
export default SupplierTab;